import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CELL_DOOR_OFF, ownerHubDoorOrigin, ownerHubToken } from '@/lib/cell/ownerHub';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { useFeature } from '@/lib/useFeature';
import { viewAsProId } from '@/lib/viewAs';
import {
  CAPTION_MAX, MAX_VIDEO_MS, STATE_WORDS, canSend, checkPick, endChoices, type PieceView, type TvPayload,
} from '@/lib/getOnTv';

/**
 * GET ON THE TV — a pro sends a piece for their studio's TV (TV-PRO-1; decision fda9ab8e, Peter 2026-09-26: "A pro
 * submits what they want to market (an Instagram post or ad, a photo, a short video, a promotion) from the Pro Hub —
 * one simple submit, with the studio's guidelines right beside the button. Nothing plays until the owner approves it…
 * Pros see their submission's status and when it is playing.").
 *
 * One page, three parts, in the order a pro needs them: the studio's guidelines beside the send button; the send
 * itself (a photo or short video from their phone, or one of their own Instagram posts); and what they have sent, with
 * where each stands — "Playing now" only when a TV really is playing it (the door asks the TV's own rules).
 *
 * The door is the Owner Hub's /pro/tv (image-os-web routes/pro.tv.ts): it checks this pro's token, their place at the
 * studio and the switch for them, refuses a tall video before storing anything, and files the piece for the owner. Nothing
 * is sent to anyone from here; the owner answers in their TV room and the answer shows on this page.
 */

// CELL (M28): the Owner Hub door for this build — doorOrigin() in production (lib/hosts.ts), the AWS staging host on
// the cell build; null there = not on the AWS staging build yet, and the door helper below says so (CELL_DOOR_OFF).
const DOOR = ownerHubDoorOrigin();

async function token(): Promise<string> {
  if (!DOOR) throw new Error(CELL_DOOR_OFF);
  // CELL (M28): this tab's own token — Supabase's in production, Cognito's access token on the cell build (lib/cell/ownerHub.ts).
  const t = await ownerHubToken();
  if (!t) throw new Error('Sign in again — no session.');
  return t;
}
async function readDoor(locationId: string, viewing: string | null): Promise<TvPayload> {
  const q = new URLSearchParams({ location_id: locationId, ...(viewing ? { view_pro: viewing } : {}) });
  const res = await fetch(`${DOOR}/pro/tv?${q}`, { headers: { Authorization: `Bearer ${await token()}` } });
  const payload = (await res.json().catch(() => null)) as (TvPayload & { error?: string }) | null;
  if (!res.ok || !payload?.ok) throw new Error(payload?.error ?? 'Could not read your TV pieces right now.');
  return payload;
}
async function send(body: FormData | Record<string, unknown>): Promise<{ ok: boolean; error?: string; status?: number }> {
  try {
    const isForm = body instanceof FormData;
    const res = await fetch(`${DOOR}/pro/tv`, {
      method: 'POST',
      // Multipart sets its own Content-Type (with the boundary); only JSON names one.
      headers: { Authorization: `Bearer ${await token()}`, ...(isForm ? {} : { 'Content-Type': 'application/json' }) },
      body: isForm ? body : JSON.stringify(body),
    });
    const out = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
    return res.ok && out?.ok ? { ok: true } : { ok: false, status: res.status, error: out?.error ?? 'That could not be sent just now. Try again in a minute.' };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'That could not be sent just now.' };
  }
}

/** A video's own length and shape, read by the browser before it is sent (the door checks the shape again). */
function readVideo(file: File): Promise<{ ms: number; width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    const done = (r: { ms: number; width: number; height: number } | null) => { URL.revokeObjectURL(url); resolve(r); };
    v.onloadedmetadata = () => done({ ms: Math.round(v.duration * 1000), width: v.videoWidth, height: v.videoHeight });
    v.onerror = () => done(null);
    v.src = url;
  });
}

export default function GetOnTv() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const { activeLocationId } = useActiveLocationId();
  const feature = useFeature('tv');
  const viewing = viewAsProId();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<TvPayload>({
    queryKey: ['pro-tv', userId, activeLocationId, viewing],
    enabled: !!userId && !!activeLocationId && feature.enabled,
    refetchInterval: 60_000,
    queryFn: () => readDoor(activeLocationId!, viewing),
  });

  if (!feature.loading && !feature.enabled) {
    return <div className="card text-sm text-image-charcoal">Getting on the TV isn&rsquo;t switched on for your studio yet.</div>;
  }
  if (feature.loading || isLoading) return <div className="card text-sm text-image-charcoal">Reading your studio&rsquo;s TV&hellip;</div>;
  if (error || !data) return <div className="card text-sm text-image-charcoal">Couldn&rsquo;t read your TV pieces right now. Try again in a minute.</div>;
  if (!data.on) return <div className="card text-sm text-image-charcoal">Getting on the TV isn&rsquo;t switched on for you yet.</div>;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['pro-tv'] });
  return (
    <div className="space-y-4" data-testid="get-on-tv">
      <div>
        <div className="text-[10px] font-extrabold tracking-[0.14em] text-image-charcoal uppercase">{data.studioName ?? 'Your studio'}</div>
        <h1 className="text-2xl font-bold text-image-black">Get on the TV</h1>
        <p className="text-sm text-image-charcoal mt-1">
          Send a photo, a short video, one of your Instagram posts or a promotion for the studio&rsquo;s TV. The owner looks at it
          first; once it&rsquo;s approved it plays for up to {data.runDays} days, sharing the screen equally with the other pros.
        </p>
      </div>
      {data.viewing ? (
        <div className="card text-sm text-image-charcoal">You&rsquo;re viewing this pro&rsquo;s page. Only they can send a piece.</div>
      ) : (
        <SendCard data={data} locationId={activeLocationId!} onSent={refresh} />
      )}
      <Sent data={data} locationId={activeLocationId!} readOnly={!!data.viewing} onChanged={refresh} />
    </div>
  );
}

function SendCard({ data, locationId, onSent }: { data: TvPayload; locationId: string; onSent: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [video, setVideo] = useState<{ ms: number; width: number; height: number } | null>(null);
  const [ig, setIg] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [promotion, setPromotion] = useState(false);
  const [endsOn, setEndsOn] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const ends = useMemo(() => endChoices(new Date(), data.runDays ?? 14), [data.runDays]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const problem = checkPick(file ? { type: file.type, size: file.size, video } : null);
  const waiting = (data.submissions ?? []).filter((s) => s.state === 'waiting').length;
  const ready = canSend({ file: !!file, instagram: !!ig, problem, waiting });

  const pick = async (f: File | null) => {
    setNote(null); setIg(null); setVideo(null);
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(f ? URL.createObjectURL(f) : null);
    if (f && f.type.startsWith('video/')) setVideo(await readVideo(f));
  };
  const reset = () => { void pick(null); setCaption(''); setPromotion(false); setEndsOn(''); if (input.current) input.current.value = ''; };

  const submit = async () => {
    if (!ready) return;
    setBusy(true); setNote(null);
    let r: { ok: boolean; error?: string };
    if (ig) {
      r = await send({ act: 'instagram', location_id: locationId, external_id: ig, caption, ends_on: endsOn || null });
    } else {
      const fd = new FormData();
      fd.append('file', file!);
      fd.append('location_id', locationId);
      fd.append('caption', caption);
      if (endsOn) fd.append('ends_on', endsOn);
      if (promotion) fd.append('promotion', 'true');
      if (video) fd.append('duration_ms', String(video.ms));
      r = await send(fd);
    }
    setBusy(false);
    if (r.ok) {
      setNote({ ok: true, text: 'Sent. The owner will look at it, and you’ll see the answer here.' });
      reset(); setIg(null);
      onSent();
    } else {
      setNote({ ok: false, text: r.error ?? 'That could not be sent.' });
    }
  };

  return (
    <div className="card space-y-4" data-testid="tv-send">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-3">
          <div className="ph-sec">Send a piece</div>
          <label className="block">
            <span className="ph-label">A photo or a video from your phone</span>
            <input ref={input} type="file" accept="image/*,video/mp4,video/quicktime" className="mt-1 block w-full text-base"
              onChange={(e) => { void pick(e.target.files?.[0] ?? null); }} data-testid="tv-file" />
          </label>
          {preview && file && (file.type.startsWith('video/')
            ? <video src={preview} className="w-full rounded-lg bg-black" muted controls playsInline />
            : <img src={preview} alt="" className="w-full rounded-lg object-cover" />)}
          {problem && <p className="ph-help !text-red-700" role="alert" data-testid="tv-problem">{problem}</p>}
          {(data.instagram ?? []).length > 0 && (
            <div>
              <span className="ph-label">Or one of your Instagram posts</span>
              <div className="mt-1 grid grid-cols-3 gap-2" role="radiogroup" aria-label="Your Instagram posts">
                {(data.instagram ?? []).map((p) => (
                  <button key={p.externalId} type="button" role="radio" aria-checked={ig === p.externalId}
                    className={`rounded-lg overflow-hidden border-2 ${ig === p.externalId ? 'border-image-black' : 'border-transparent'}`}
                    onClick={() => { void pick(null); setIg(ig === p.externalId ? null : p.externalId); setNote(null); }}>
                    <img src={p.imageUrl} alt={p.caption ?? 'Your Instagram post'} className="aspect-square w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
          <label className="block">
            <span className="ph-label">Your words for the TV (optional)</span>
            <input className="input mt-1 text-base" value={caption} maxLength={CAPTION_MAX} onChange={(e) => setCaption(e.target.value)}
              placeholder="Fall color special, 20% off" data-testid="tv-caption" />
            <span className="ph-help">{caption.length} of {CAPTION_MAX}</span>
          </label>
          {!ig && (
            <label className="flex items-center gap-2 ph-choice">
              <input type="checkbox" checked={promotion} onChange={(e) => setPromotion(e.target.checked)} />
              This is a promotion or a special
            </label>
          )}
          <div>
            <span className="ph-label">Stop showing it</span>
            <div className="mt-1 flex flex-wrap gap-2">
              {ends.map((c) => (
                <button key={c.label} type="button" onClick={() => setEndsOn(c.date)}
                  className={`rounded-full border px-3 py-1.5 ph-choice ${endsOn === c.date ? 'border-image-black font-semibold' : 'border-image-border'}`}>
                  {c.label}
                </button>
              ))}
            </div>
            <label className="block mt-2">
              <span className="ph-help">Or pick the day</span>
              <input type="date" className="input mt-1 text-base" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} data-testid="tv-ends-on" />
            </label>
          </div>
        </div>
        <div className="rounded-xl bg-image-fog p-4" data-testid="tv-guidelines">
          <div className="ph-sec">The studio&rsquo;s guidelines</div>
          <ul className="mt-2 space-y-1.5 text-sm text-image-dark-charcoal list-disc pl-5">
            {(data.guidelines ?? '').split('\n').map((l) => l.trim()).filter(Boolean).map((l, i) => <li key={i}>{l}</li>)}
          </ul>
          <p className="ph-help mt-3">
            Up to {data.maxLivePerPro} of your pieces can play at once. Every pro gets the same share of the screen.
          </p>
          <button type="button" className="btn-primary w-full mt-4" disabled={!ready || busy} onClick={() => { void submit(); }} data-testid="tv-send-button">
            {busy ? 'Sending…' : 'Send to the owner'}
          </button>
          {waiting >= 5 && <p className="ph-help mt-2">You have 5 pieces waiting. Wait for an answer, or take one back below.</p>}
          {note && <p className={`ph-help mt-2 ${note.ok ? '' : '!text-red-700'}`} role="status">{note.text}</p>}
        </div>
      </div>
      <p className="ph-help">Videos must be wide (landscape) and {MAX_VIDEO_MS / 1000} seconds or less. They play without sound.</p>
    </div>
  );
}

function Sent({ data, locationId, readOnly, onChanged }: { data: TvPayload; locationId: string; readOnly: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  // HUB-054 / LIVE-011: a refused or dropped "Take it back" used to vanish — the button re-enabled
  // and the piece stayed queued for the owner with no word. The answer is now shown on the piece.
  const [errs, setErrs] = useState<Record<string, string>>({});
  const list = data.submissions ?? [];
  const withdraw = async (id: string) => {
    setBusy(id); setErrs((m) => ({ ...m, [id]: '' }));
    const r = await send({ act: 'withdraw', location_id: locationId, id });
    setBusy(null);
    if (r.ok) { onChanged(); return; }
    setErrs((m) => ({ ...m, [id]: `It was not taken back. ${r.error ?? ''}`.trim() }));
    // 409 = the owner answered in the meantime; re-read so the card shows where it really stands.
    if (r.status === 409) onChanged();
  };
  return (
    <div className="card space-y-3" data-testid="tv-sent">
      <div className="ph-sec">What you&rsquo;ve sent</div>
      {!list.length && <p className="text-sm text-image-charcoal">Nothing yet. Pieces you send show here, with the owner&rsquo;s answer.</p>}
      <ul className="space-y-3">
        {list.map((s) => (
          <li key={s.id} className="flex gap-3 items-start" data-testid="tv-piece">
            <Thumb s={s} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${s.state === 'playing' ? 'bg-emerald-600 text-white' : 'bg-image-fog text-image-dark-charcoal'}`}
                  data-testid="tv-state">{STATE_WORDS[s.state]}</span>
                {s.caption && <span className="text-sm font-semibold text-image-black truncate">{s.caption}</span>}
              </div>
              <PieceLine s={s} />
              {s.note && (s.state === 'sent_back' || s.state === 'declined') && (
                <p className="text-sm text-image-dark-charcoal mt-1">The owner says: {s.note}</p>
              )}
              {!readOnly && s.state === 'waiting' && (
                <button type="button" className="btn-ghost mt-1 text-sm" disabled={busy === s.id}
                  onClick={() => { void withdraw(s.id); }}>
                  {busy === s.id ? 'Taking it back…' : 'Take it back'}
                </button>
              )}
              {errs[s.id] && <p role="alert" className="text-sm text-red-700 mt-1" data-testid="tv-withdraw-error">{errs[s.id]}</p>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Thumb({ s }: { s: PieceView }) {
  if (!s.mediaUrl) return <div className="size-16 rounded-lg bg-image-fog shrink-0" aria-hidden="true" />;
  return s.mediaKind === 'video'
    ? <video src={s.mediaUrl} className="size-16 rounded-lg object-cover bg-black shrink-0" muted playsInline preload="metadata" aria-hidden="true" />
    : <img src={s.mediaUrl} alt="" className="size-16 rounded-lg object-cover shrink-0" />;
}

function PieceLine({ s }: { s: PieceView }) {
  const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
  const text = s.state === 'playing' ? `On ${s.playingOn.join(', ')} until ${day(s.endsAt)}`
    : s.state === 'approved' ? `Approved until ${day(s.endsAt)}. It plays when the owner has the pros’ playlist on a TV.`
      : s.state === 'ended' ? `Ended ${day(s.endsAt)}`
        : s.state === 'waiting' ? `Sent ${day(s.createdAt)}${s.requestedEndsAt ? `, to stop by ${day(s.requestedEndsAt)}` : ''}`
          : `Answered ${day(s.decidedAt)}`;
  return <p className="ph-help mt-0.5">{text}</p>;
}
